import { TouchEvent as ReactTouchEvent, useContext, useRef, useState } from 'react'
import classNames from 'classnames'
import { AccountCircleIcon } from '@/atoms/Icons/AccountCircleIcon'
import { UpArrowCircleIcon } from '@/atoms/Icons/UpArrowCircleIcon'
import { Image } from '@/atoms/Image'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { ParagraphMD, ParagraphSM, TitleMD } from '@/atoms/Text'
import { ProjectMetaContext } from '@/contexts/ProjectMetaContext/ProjectMetaContext'
import { CatalogCast, isSeriesCast } from '@/services/ContentCatalog'
import { useTranslate } from '@/utils/translate/translate-client'

type MaybeCastLike = Partial<CatalogCast> & { job?: string }
type TitleWithPeople = { cast?: unknown; crew?: unknown; seasons?: unknown }

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object'

const makeKey = (p: MaybeCastLike): string => {
  if (!p || typeof p !== 'object') return ''
  const anyP = p as Record<string, unknown>
  const id =
    (anyP.id as string | number | undefined) ??
    (isObject(anyP.person) ? ((anyP.person as Record<string, unknown>).id as string | number | undefined) : undefined)
  const name =
    (anyP.name as string | undefined) ??
    (isObject(anyP.person) ? ((anyP.person as Record<string, unknown>).name as string | undefined) : undefined)
  const job = (anyP.job as string | undefined) ?? ''
  return [id ?? '', name ?? '', job].join('|')
}

const dedupe = (items: MaybeCastLike[]) => {
  const seen = new Set<string>()
  const out: MaybeCastLike[] = []
  for (const item of items) {
    if (!item) continue
    const key = makeKey(item)
    if (!key) {
      out.push(item)
      continue
    }
    if (seen.has(key)) continue
    seen.add(key)
    out.push(item)
  }
  return out
}

function seasonEdgesFromTitle(title: TitleWithPeople | null): Record<string, unknown>[] {
  const seasons = (title as Record<string, unknown> | null)?.seasons
  if (!isObject(seasons)) return []
  const edges = (seasons as Record<string, unknown>).edges
  return Array.isArray(edges) ? (edges as Record<string, unknown>[]) : []
}

function accumulateEpisodePeople(
  seasonEdges: Record<string, unknown>[],
  episodeCast: MaybeCastLike[],
  episodeCrew: MaybeCastLike[],
): void {
  seasonEdges.forEach((seasonEdge) => {
    if (!isObject(seasonEdge) || !isObject(seasonEdge.node)) return
    const seasonNode = seasonEdge.node as Record<string, unknown>
    const episodes = seasonNode.episodes
    if (!isObject(episodes) || !Array.isArray((episodes as Record<string, unknown>).edges)) return
    ;((episodes as { edges: unknown[] }).edges as unknown[]).forEach((episodeEdge) => {
      if (!isObject(episodeEdge) || !isObject(episodeEdge.node)) return
      const episodeNode = episodeEdge.node as Record<string, unknown>

      const epCastRaw = Array.isArray(episodeNode.cast) ? (episodeNode.cast as unknown[]) : []
      const epCrewRaw = Array.isArray(episodeNode.crew) ? (episodeNode.crew as unknown[]) : []

      epCastRaw.forEach((c) => {
        if (c) episodeCast.push(c as MaybeCastLike)
      })
      epCrewRaw.forEach((c) => {
        if (c) episodeCrew.push(c as MaybeCastLike)
      })
    })
  })
}

function collectEpisodePeople(title: TitleWithPeople | null): {
  episodeCast: MaybeCastLike[]
  episodeCrew: MaybeCastLike[]
} {
  const episodeCast: MaybeCastLike[] = []
  const episodeCrew: MaybeCastLike[] = []

  const seasonEdges = seasonEdgesFromTitle(title)
  if (seasonEdges.length === 0) {
    return { episodeCast, episodeCrew }
  }

  accumulateEpisodePeople(seasonEdges, episodeCast, episodeCrew)

  return { episodeCast, episodeCrew }
}

function extractCastCrew(project: unknown): { cast: MaybeCastLike[]; crew: MaybeCastLike[] } {
  if (!isObject(project)) return { cast: [], crew: [] }

  const projectData = isObject((project as Record<string, unknown>).projectData)
    ? ((project as Record<string, unknown>).projectData as Record<string, unknown>)
    : null
  const title = projectData && isObject(projectData.title) ? (projectData.title as TitleWithPeople) : null

  const directCast = Array.isArray(title?.cast) ? (title!.cast as unknown[]) : []
  const directCrew = Array.isArray(title?.crew) ? (title!.crew as unknown[]) : []

  const { episodeCast, episodeCrew } = collectEpisodePeople(title)

  const combinedCast = [...directCast, ...episodeCast] as MaybeCastLike[]
  const combinedCrew = [...directCrew, ...episodeCrew] as MaybeCastLike[]

  return {
    cast: dedupe(combinedCast).filter(Boolean),
    crew: dedupe(combinedCrew).filter(Boolean),
  }
}

export function CastSection() {
  const { project } = useContext(ProjectMetaContext)

  const ref = useRef<HTMLDivElement>(null)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const minSwipeDistance = 50
  const { t } = useTranslate('watch')

  const { cast: castList, crew: crewList } = extractCastCrew(project)
  const merged: MaybeCastLike[] = [...castList, ...crewList]

  if (merged.length === 0) return null

  const getName = (p: MaybeCastLike): string | undefined => {
    if (typeof p.name === 'string' && p.name.trim()) return p.name
    const personName = (p as unknown as { person?: { name?: string } })?.person?.name
    if (typeof personName === 'string' && personName.trim()) return personName
    return undefined
  }

  const getSubtitle = (p: MaybeCastLike): string | undefined => {
    if (isSeriesCast(p as CatalogCast)) {
      const chars = (p as unknown as { characters?: string[] })?.characters
      if (Array.isArray(chars) && typeof chars[0] === 'string' && chars[0].trim()) return chars[0]
    }
    const character = (p as unknown as { character?: string })?.character
    if (typeof character === 'string' && character.trim()) return character
    const job = p.job
    if (typeof job === 'string' && job.trim()) return job
    return undefined
  }

  const rawPeople = merged
    .map((p) => {
      const name = getName(p)
      if (!name) return null
      return { data: p, name, subtitle: getSubtitle(p) }
    })
    .filter((v): v is { data: MaybeCastLike; name: string; subtitle: string } => v !== null)

  type Aggregated = { data: MaybeCastLike; name: string; subtitles: string[] }
  const aggregationMap = new Map<string, Aggregated>()

  const personKey = (p: { data: MaybeCastLike; name: string }) => {
    const anyP = p.data as Record<string, unknown>
    const id =
      (anyP.id as string | number | undefined) ??
      (isObject(anyP.person) ? ((anyP.person as Record<string, unknown>).id as string | number | undefined) : undefined)
    return String(id ?? p.name).trim()
  }

  for (const entry of rawPeople) {
    if (!entry) continue
    const key = personKey(entry)
    let agg = aggregationMap.get(key)
    if (!agg) {
      agg = { data: entry.data, name: entry.name, subtitles: [] }
      aggregationMap.set(key, agg)
    }
    if (entry.subtitle) {
      const normalized = entry.subtitle.trim()
      if (normalized && !agg.subtitles.some((s) => s.toLowerCase() === normalized.toLowerCase())) {
        agg.subtitles.push(normalized)
      }
    }
  }

  const people = Array.from(aggregationMap.values()).map((p) => ({
    data: p.data,
    name: p.name,
    subtitle: p.subtitles.join(', '),
  }))

  const onTouchStart = (event: ReactTouchEvent) => {
    setTouchEnd(0)
    const start = event.targetTouches[0]?.clientX
    if (typeof start === 'number') setTouchStart(start)
  }

  const onTouchMove = (event: ReactTouchEvent) => {
    const end = event.targetTouches[0]?.clientX
    if (typeof end === 'number') setTouchEnd(end)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    isLeftSwipe ? handleRightDirection() : handleLeftDirection()
  }

  const handleLeftDirection = () => {
    const scrollLeft = ref?.current?.scrollLeft ?? 0
    const scrollTo = Math.max(0, scrollLeft - 240)
    ref?.current?.scrollTo({ top: 0, left: scrollTo, behavior: 'smooth' })
  }

  const handleRightDirection = () => {
    const scrollLeft = ref?.current?.scrollLeft ?? 0
    const scrollTo = Math.max(0, scrollLeft + 240)
    ref?.current?.scrollTo({ top: 0, left: scrollTo, behavior: 'smooth' })
  }

  return (
    <div>
      <PaddedContainer fluid>
        <div className="flex items-center justify-between pb-2">
          <TitleMD color="white" weight="semibold" className="uppercase">
            {t('castAndCrew', 'Cast & Crew')}
          </TitleMD>
          <div
            className={classNames('flex items-center gap-3', {
              'md:hidden': people.length <= 3,
            })}
          >
            <div onClick={handleLeftDirection}>
              <UpArrowCircleIcon
                className="-rotate-90 cursor-pointer !stroke-white transition-all duration-300 ease-in-out hover:!stroke-white lg:!stroke-core-gray-300"
                color="inherit"
                size={32}
              />
            </div>
            <div onClick={handleRightDirection}>
              <UpArrowCircleIcon
                className="rotate-90 cursor-pointer !stroke-white transition-all duration-300 ease-in-out hover:!stroke-white lg:!stroke-core-gray-300"
                color="inherit"
                size={32}
              />
            </div>
          </div>
        </div>
      </PaddedContainer>
      <div
        className="flex items-start gap-4 overflow-hidden px-4 sm:px-8 md:px-12 xl:px-16"
        ref={ref}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {people.map(({ name, subtitle, data }, i) => {
          const headshotUrl =
            Array.isArray(data?.images) &&
            data.images[0] &&
            typeof data.images[0] === 'object' &&
            data.images[0] !== null &&
            'cloudinaryPath' in data.images[0]
              ? (data.images[0] as { cloudinaryPath?: string }).cloudinaryPath ?? null
              : null

          return (
            <div key={`${name}-${i}`} className="flex w-36 shrink-0 flex-col items-center justify-center">
              <div className="relative mx-auto h-[120px] w-[120px] select-none overflow-hidden rounded-full">
                {headshotUrl ? (
                  <Image alt={name} src={headshotUrl} height={120} width={120} />
                ) : (
                  <div className="bg-core-gray-800">
                    <AccountCircleIcon
                      aria-label="generic-profile-picture"
                      size={120}
                      color1={'core-gray-500'}
                      color2={'core-gray-700'}
                    />
                  </div>
                )}
              </div>
              <div className="w-full text-center">
                <ParagraphMD
                  color="white"
                  weight="bold"
                  className="select-none overflow-hidden text-ellipsis whitespace-nowrap"
                  title={name}
                >
                  {name}
                </ParagraphMD>
                {subtitle ? (
                  <ParagraphSM color="core-gray-300" className="line-clamp-2 select-none overflow-hidden text-ellipsis">
                    {subtitle}
                  </ParagraphSM>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
